import { CreditCard } from "lucide-react";
import { useEffect, useState } from "react";
import { useLanguage } from "../../i18n/language-provider";
import { fetchBillingStatus, type BillingStatusInfo } from "../../services/billingApi";

const DAY_MS = 86_400_000;

/** Trials this close to the end color as a warning. */
const TRIAL_WARNING_DAYS = 7;

interface BillingStatusChipProps {
    /** Dot-only variant for the collapsed sidebar rail. */
    compact?: boolean;
    /** Re-fetch when this changes (e.g. the active connection switched). */
    refreshKey?: unknown;
}

/**
 * The sidebar's billing indicator — subscription status where the credits
 * count used to be. Self-fetching and silent on failure: a fresh account
 * without a configured PMS simply shows nothing.
 */
export function BillingStatusChip({ compact = false, refreshKey }: BillingStatusChipProps) {
    const { t } = useLanguage();
    const b = t.billing;
    const [status, setStatus] = useState<BillingStatusInfo | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchBillingStatus()
            .then((info) => {
                if (!cancelled) setStatus(info);
            })
            .catch(() => {
                if (!cancelled) setStatus(null);
            });
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    if (!status) return null;

    const trialDaysLeft =
        status.status === "trialing"
            ? Math.max(0, Math.ceil((new Date(status.trialEnd).getTime() - Date.now()) / DAY_MS))
            : null;

    const label =
        status.status === "active"
            ? b.statusActive
            : status.status === "unpaid"
              ? b.statusUnpaid
              : b.trialDaysLeft.replace("{count}", String(trialDaysLeft));

    const colorClass =
        status.status === "unpaid"
            ? "text-destructive"
            : status.status === "active"
              ? "text-up"
              : trialDaysLeft !== null && trialDaysLeft <= TRIAL_WARNING_DAYS
                ? "text-warning"
                : "text-sidebar-foreground";

    if (compact) {
        // Theme colours all, and the ones the sync chip's dot takes on the
        // same rail. `text-up` above is a hand-written helper with no `bg-`
        // twin: `bg-up` is no class at all, and it drew an active
        // subscription as a dot nobody could see.
        const dotClass =
            status.status === "unpaid"
                ? "bg-destructive"
                : status.status === "active"
                  ? "bg-success"
                  : trialDaysLeft !== null && trialDaysLeft <= TRIAL_WARNING_DAYS
                    ? "bg-warning"
                    : "bg-sidebar-primary";
        return (
            <span
                data-billing-dot
                aria-hidden
                className={`inline-block h-1.5 w-1.5 rounded-full ${dotClass}`}
            />
        );
    }

    return (
        <div className="flex items-center gap-1.5 mt-0.5">
            <CreditCard className="h-3.5 w-3.5 text-sidebar-primary" />
            <span className={`text-sm font-medium ${colorClass}`}>{label}</span>
        </div>
    );
}
